import ColorField from './ColorField';
import '../css/SlotButtonColorSettings.css';
import {
  SLOT_BUTTON_STATUSES,
  SLOT_COLOR_PARTS,
  SLOT_COLOR_FOLLOW_ACTIVE,
  SLOT_COLOR_BORDER_SHADE,
  SLOT_COLOR_HATCH_SHADE,
  SLOT_COLOR_FOLLOW_ACTIVE_LABEL,
} from '../constants/bookingConst';
import {
  getEffectiveSlotColor,
  setSlotButtonColor,
  followsActiveColor,
  setFollowsActiveColor,
} from '../utils/slotButtonColorUtil';
import { shadeHex } from '../utils/colorUtil';

/**
 * Inline style for one preview button, drawn the way the guest grid draws
 * that state (see TimeSlotSelection.css): a darker edge on every state except
 * the solid Selected / Open Play fills, and the diagonal hatch on Closed.
 *
 * @param {string} statusKey
 * @param {string} background - effective background hex
 * @param {string} text - effective text hex
 * @returns {object} React style object
 */
const buildPreviewStyle = (statusKey, background, text) => {
  const solidFill = statusKey === 'selected' || statusKey === 'openPlay';
  const style = {
    background,
    color: text,
    borderColor: solidFill ? background : shadeHex(background, SLOT_COLOR_BORDER_SHADE),
  };
  if (statusKey === 'unavailable') {
    const stripe = shadeHex(background, SLOT_COLOR_HATCH_SHADE);
    style.background = `repeating-linear-gradient(45deg, ${background}, ${background} 6px, ${stripe} 6px, ${stripe} 12px)`;
  }
  return style;
};

/**
 * Config -> Theme -> Time Slot Button Colors: a background and a font colour
 * for every state of the guest booking grid's slot buttons, each with a live
 * preview, plus a Reset that puts every state back to its default colours.
 *
 * Edits only the form — nothing reaches guests until Save Configuration,
 * which is also where the change is written to the audit trail.
 *
 * @param {object} props
 * @param {object|undefined} props.value - formData.slotButtonColors
 * @param {string} props.activeColor - the form's Active Button Color, which
 *   Selected follows by default
 * @param {(next: object) => void} props.onChange - receives the whole new map
 * @returns {JSX.Element}
 */
function SlotButtonColorSettings({ value, activeColor, onChange }) {
  const hasCustomColors = Object.keys(value || {}).length > 0;

  return (
    <div className="sbc-section">
      <div className="sbc-head">
        <div>
          <h3 className="sbc-title">Time Slot Button Colors</h3>
          <p className="sbc-desc">
            Background and font colors of the time slot buttons on the guest booking screen. Changes apply once you
            press &quot;Save Configuration&quot; and redeploy.
          </p>
        </div>
        <button
          type="button"
          className="sbc-reset"
          onClick={() => onChange({})}
          disabled={!hasCustomColors}
        >
          Reset to default colors
        </button>
      </div>

      <div className="sbc-list">
        {SLOT_BUTTON_STATUSES.map((status) => {
          const background = getEffectiveSlotColor(value, status.key, 'background', activeColor);
          const text = getEffectiveSlotColor(value, status.key, 'text', activeColor);

          return (
            <div className="sbc-row" key={status.key}>
              <div className="sbc-preview-cell">
                <span className="sbc-row-label">{status.label}</span>
                <span className="sbc-preview" style={buildPreviewStyle(status.key, background, text)}>
                  {status.sample}
                </span>
              </div>
              {SLOT_COLOR_PARTS.map((part) => {
                // Selected's background can stay in sync with Config ->
                // Theme -> Active Button Color (the default) or take its own
                // colour; the picker is locked while it is in sync.
                const followable = status.defaults[part.key] === SLOT_COLOR_FOLLOW_ACTIVE;
                const following = followable && followsActiveColor(value, status.key, part.key);
                return (
                  <div className="sbc-field" key={part.key}>
                    {followable && (
                      <label className="sbc-follow">
                        <input
                          type="checkbox"
                          checked={following}
                          onChange={(e) => onChange(
                            setFollowsActiveColor(value, status.key, part.key, e.target.checked, activeColor),
                          )}
                        />
                        {SLOT_COLOR_FOLLOW_ACTIVE_LABEL}
                      </label>
                    )}
                    <ColorField
                      label={part.label}
                      value={part.key === 'background' ? background : text}
                      disabled={following}
                      onChange={(next) => onChange(setSlotButtonColor(value, status.key, part.key, next, activeColor))}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SlotButtonColorSettings;
